import { Row, useColumnOrder, usePagination, useSortBy, useTable } from 'react-table'
import { useRowSelect } from 'core/table/table-hooks/useRowSelect'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useChampionColumns, { useStyles } from 'champion/hooks/useChampionColumns'
import { useDateRangeFilter } from 'filtering/hooks'
import Grid from '@material-ui/core/Grid'
import MessageOutlinedIcon from '@material-ui/icons/MessageOutlined'
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined'
import useMessageRecipientsPrompt from 'messages/hooks/useMessageRecipientsPrompt'
import Loading from 'components/Loading'
import { RouteComponentProps } from 'react-router'
import { TableMenuButton } from 'civic-champs-shared/core/TableMenuButton'
import ExportChampionsButton from 'champion/components/ExportChampionsButton'
import { useHasRole } from 'auth/hooks'
import { INTERNAL_ADMIN, SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { ExtendedPagedTable } from 'core/table/components/ExtendedPagedTable'
import AddIcon from '@material-ui/icons/Add'
import ContainedButton from 'civic-champs-shared/core/ContainedButton'
import useGetColumnState from 'core/table/table-hooks/useGetColumnState'
import TablePopoverButton from 'civic-champs-shared/core/TablePopoverButton'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { SendNotificationModal } from 'champion/components/SendNotificationModal'
import useSendNotification from 'champion/hooks/useSendNotification'
import first from 'lodash/first'
import isEqual from 'lodash/isEqual'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { useFeatureEnabled } from 'core/feature/hooks'
import { useGetSearchableContactsFromVolunteersArray } from 'messages/hooks/useGetSearchableContactsFromVolunteersArray'
import { ReactComponent as VerifiedIcon } from 'images/verified.svg'
import { useHandleChat } from 'chats'
import { Volunteer } from 'champion/utils/interface'
import LabelOutlinedIcon from '@material-ui/icons/LabelOutlined'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import { useAddRemoveTagsPrompt } from '../hooks/useAddRemoveTagsPrompt'
import { Person } from '../../people/interface'
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined'
import useMultiGroupAddMembersPrompt from 'group/hooks/useMultiGroupAddMembersPrompt'
import usePushChampions from '../hooks/usePushChampions'
import { IntegrationKey, IntegrationProvider } from '../../integrations/types'
import useGetIntegrationKey, { emptyValue } from '../../integrations/hooks/useGetIntegrationKey'
import SaveAltOutlinedIcon from '@material-ui/icons/SaveAltOutlined'
import ImportVolunteersPrompt from '../components/ImportVolunteersPrompt'
import { ExportChampionsMenu } from '../components/ExportChampionsMenu'
import { useNationalAvgVolunteerValuePerHour } from 'champion/hooks/useNationalAvgVolunteerValuePerHour'
import { useFetchChampions } from 'champion/hooks/useFetchChampions'
import useAddVolunteerPrompt from 'volunteering/volunteers/hooks/useAddVolunteerPrompt'
import useRemoveVolunteerPrompt from 'volunteering/volunteers/hooks/useRemoveVolunteerPrompt'
import { buildChampionsHeaderSummary, championsHiddenColumns, championsTableName } from 'champion/championsTableConfig'
import { useChampionsPageI18n } from 'champion/hooks/useChampionsPageI18n'
import { buildChampionColumnFiltersParam } from 'champion/utils/championColumnFiltersParam'
import { useServerSideChampionsSelectColumn } from 'champion/hooks/useServerSideChampionsSelectColumn'
import { useChampionsServerSideSelectionPlugin } from 'champion/hooks/useChampionsServerSideSelectionPlugin'
import { ChampionsServerSideSelectionProvider } from 'champion/context/ChampionsServerSideSelectionContext'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'

export const ChampionsServerSide = ({ location, history }: RouteComponentProps) => {
  const skipReset = useRef<boolean>()
  const isSuperAdmin = useHasRole(SUPER_ADMIN)
  const isInternalAdmin = useHasRole(INTERNAL_ADMIN)
  const [dateRange] = useDateRangeFilter()
  const showMessageRecipientsPrompt = useMessageRecipientsPrompt()
  const [includeInactive, setIncludeInactive] = useState(true)
  const columnFiltersParam = useMemo(() => buildChampionColumnFiltersParam(location.search), [location.search])
  const {
    volunteers,
    totalCount,
    pagedSummary,
    loading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    sortBy,
    sortPreferencesReady,
    onSearchChange,
    onSortChange,
    refetch,
    filterKey,
    selectedRowIds,
    selectedCount,
    selectingAll,
    selectionPluginApiRef,
    clearSelection,
    selectAllMatching,
    getSelectedRows,
  } = useFetchChampions(dateRange, includeInactive, columnFiltersParam)
  const columns = useChampionColumns()
  const columnStyles = useStyles()
  const getSearchableContacts = useGetSearchableContactsFromVolunteersArray(volunteers)
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const showNotificationsModal = useShowPrompt(SendNotificationModal)
  const organization = useCurrentOrg()
  const valuePerHour = useNationalAvgVolunteerValuePerHour()
  const { labels, cards, getOperatorOptions } = useChampionsPageI18n(valuePerHour)
  const championsTagsEnabled = useFeatureEnabled('ChampionsTags')
  const chatsEnabled = useFeatureEnabled('Chats')
  const handleTagsChange = useCallback(async () => {
    await refetch()
  }, [refetch])
  const showTagsPrompt = useAddRemoveTagsPrompt(handleTagsChange)
  const isLglIntegrationEnabled = useFeatureEnabled('LglIntegration')
  const isFastnIntegrationEnabled = useFeatureEnabled('FastnIntegration')
  const isDonorPerfectIntegrationEnabled = useFeatureEnabled('DonorPerfectIntegration')
  const lglChampionsSyncEnabled = useFeatureEnabled('ChampionsLGLSync')
  const bulkAddMembersEnabled = useFeatureEnabled('BulkAddMembers')
  const newVolunteerImportEnabled = useFeatureEnabled('NewVolunteerImport')
  const newVolunteerImportForSuperAdminEnabled = useFeatureEnabled('NewVolunteerImportForSuperAdmin')
  const showImportButton =
    newVolunteerImportEnabled && (isInternalAdmin || (newVolunteerImportForSuperAdminEnabled && isSuperAdmin))
  const pushChampions = usePushChampions()
  const showImportVolunteerPrompt = useShowPrompt(ImportVolunteersPrompt)

  const [integrationKey, setIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const [fastnIntegrationKey, setFastnIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const [donorPerfectIntegrationKey, setDonorPerfectIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const fetchIntegrationKey = useGetIntegrationKey()
  useEffect(() => {
    fetchIntegrationKey(IntegrationProvider.LGL).then(setIntegrationKey)
    fetchIntegrationKey(IntegrationProvider.FASTN).then(setFastnIntegrationKey)
    fetchIntegrationKey(IntegrationProvider.DONOR_PERFECT).then(setDonorPerfectIntegrationKey)
  }, [fetchIntegrationKey])

  const optimisticRefresh = useCallback(() => {
    refetch()
    return () => refetch()
  }, [refetch])
  const addVolunteer = useAddVolunteerPrompt(() => refetch())
  const removeVolunteer = useRemoveVolunteerPrompt(optimisticRefresh)
  const handleGroupsAdded = useCallback(async () => {
    await refetch()
  }, [refetch])
  const showAddMembersPrompt = useMultiGroupAddMembersPrompt(handleGroupsAdded)

  const [sendNotification, { loading: sendNotificationLoading }] = useSendNotification(organization.id)
  const handleResendVerification = useCallback(
    (rows: any[]) => {
      const data = rows.map(({ original }) => original)
      const {
        user: { id, email, phoneNumber },
      } = first(data)
      if (rows.length > 1 || (!email && !phoneNumber)) {
        showNotificationsModal({ persons: data })
      } else {
        sendNotification([{ personId: id, sendMethod: email ? 'email' : 'phone' }])
      }
    },
    [sendNotification, showNotificationsModal],
  )

  useEffect(() => {
    fetchColumnState(championsTableName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const pageCount = totalCount === 0 ? 0 : Math.ceil(totalCount / pageSize)
  const tableDispatchRef = useRef<(action: { type: string; selectedRowIds?: Record<string, boolean> }) => void>(
    () => undefined,
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageIndex(newPage)
    },
    [setPageIndex],
  )

  const championsSelectionPlugin = useChampionsServerSideSelectionPlugin(selectionPluginApiRef)

  const selectionContextValue = useMemo(
    () => ({
      totalCount,
      selectedCount,
      selectingAll,
      listLoading: loading,
      onSelectAllMatching: () => selectAllMatching(tableDispatchRef.current),
      onClearSelection: () => clearSelection(tableDispatchRef.current),
    }),
    [clearSelection, loading, selectAllMatching, selectedCount, selectingAll, totalCount],
  )

  const table = useTable(
    {
      initialState: {
        hiddenColumns: championsHiddenColumns,
        pageIndex,
        pageSize,
        sortBy,
        selectedRowIds,
      },
      data: volunteers,
      columns,
      manualPagination: true,
      manualSortBy: true,
      pageCount,
      autoResetSortBy: false,
      autoResetPage: false,
      autoResetSelectedRows: false,
      getRowId: (row: Volunteer) => String(row.user.id),
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    championsSelectionPlugin,
    useServerSideChampionsSelectColumn(() => true, columnStyles.selectColumn),
  )

  tableDispatchRef.current = table.dispatch

  useEffect(() => {
    clearSelection(tableDispatchRef.current)
  }, [filterKey, clearSelection])

  useEffect(() => {
    if (table.state.pageIndex !== pageIndex) {
      table.gotoPage(pageIndex)
    }
  }, [pageIndex, table])

  useEffect(() => {
    if (table.state.pageSize !== pageSize) {
      table.setPageSize(pageSize)
    }
  }, [pageSize, table])

  const lastReportedSort = useRef(sortBy)
  const persistedSortApplied = useRef(false)

  // react-table only reads initialState.sortBy on mount, while the saved sort arrives
  // later from user preferences, so it has to be pushed in — but exactly once. Pushing
  // on every mismatch lets the table state and sortBy overwrite each other in a loop.
  useEffect(() => {
    if (persistedSortApplied.current || !sortPreferencesReady) {
      return
    }
    persistedSortApplied.current = true
    lastReportedSort.current = sortBy
    if (!isEqual(table.state.sortBy, sortBy)) {
      table.setSortBy(sortBy as { id: string; desc?: boolean }[])
    }
  }, [sortBy, sortPreferencesReady, table])

  useEffect(() => {
    if (!persistedSortApplied.current) {
      return
    }
    const tableSortBy = table.state.sortBy as { id: string; desc: boolean }[]
    if (isEqual(tableSortBy, lastReportedSort.current)) {
      return
    }
    lastReportedSort.current = tableSortBy
    onSortChange(tableSortBy)
  }, [table.state.sortBy, onSortChange])

  const summary = useMemo(
    () => buildChampionsHeaderSummary({ ...pagedSummary, count: totalCount }, valuePerHour),
    [pagedSummary, totalCount, valuePerHour],
  )

  const [selectedRows, selectedRowsCount, selectedUnverifiedRows, selectedUnverifiedRowsCount] = useMemo(() => {
    const selectedRows = getSelectedRows()
    const selectedUnverifiedRows = selectedRows.filter(
      ({
        original: {
          user: { emailVerified, phoneNumberVerified },
        },
      }) => !emailVerified && !phoneNumberVerified,
    )
    return [selectedRows, selectedCount, selectedUnverifiedRows, selectedUnverifiedRows.length]
  }, [getSelectedRows, selectedCount])

  const handlePushChampions = useCallback(async () => {
    await pushChampions({
      provider: IntegrationProvider.LGL,
      personIds: selectedRows.map((row: Row<Volunteer>) => row.original.user.id),
    })
  }, [pushChampions, selectedRows])

  const handlePushChampionsToFastn = useCallback(async () => {
    await pushChampions({
      provider: IntegrationProvider.FASTN,
      personIds: selectedRows.map((row: Row<Volunteer>) => row.original.user.id),
    })
  }, [pushChampions, selectedRows])

  const handlePushChampionsToDonorPerfect = useCallback(async () => {
    await pushChampions({
      provider: IntegrationProvider.DONOR_PERFECT,
      personIds: selectedRows.map((row: Row<Volunteer>) => row.original.user.id),
    })
  }, [pushChampions, selectedRows])

  const onMessageVolunteers = () => {
    const recipientPersonIds = selectedRows.map((row: any) => row.original.user.id)
    showMessageRecipientsPrompt({
      recipientPersonIds,
      getSearchableContacts,
      autocompleteSectionTitle: labels.allVolunteers,
    })
  }

  const onRemoveVolunteer = async () => {
    await removeVolunteer(selectedRows.map((row: any) => row.original))
    clearSelection(table.dispatch)
  }

  const onUpdateTags = useCallback(() => {
    showTagsPrompt(selectedRows.map((row: any) => row.original.user as Person))
  }, [selectedRows, showTagsPrompt])

  const [handleChat, processing] = useHandleChat({ selectedRows, history })

  const onBulkAddMembers = useCallback(async () => {
    const selectedPersons = selectedRows.map((row: any) => row.original.user as Person)
    await showAddMembersPrompt(selectedPersons)
  }, [selectedRows, showAddMembersPrompt])

  const booleanFilters = useMemo(
    () => [{ name: labels.showAllChampions, value: includeInactive, onChange: setIncludeInactive }],
    [includeInactive, labels.showAllChampions],
  )

  if (columnStateLoading || processing || (loading && volunteers.length === 0)) {
    return <Loading />
  }

  return (
    <ChampionsServerSideSelectionProvider value={selectionContextValue}>
      <Grid container>
        <Dialog open={selectingAll}>
          <DialogContent
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              paddingTop: 24,
              paddingBottom: 24,
            }}
          >
            <CircularProgress size={24} />
            <Typography component="span" style={{ lineHeight: '24px' }}>
              {labels.loadingChampions}
            </Typography>
          </DialogContent>
        </Dialog>
        <ExtendedPagedTable<Volunteer>
          summary={summary}
          cards={cards}
          history={history}
          location={location}
          table={table}
          columns={columns}
          binaryFilters={booleanFilters}
          columnState={columnState}
          tableName={championsTableName}
          searchPlaceholder={labels.searchPlaceholder}
          filterSubHeader={labels.filterSubHeader}
          useGlobalSearch
          useFilters
          getOperatorOptions={getOperatorOptions}
          serverSide
          totalCount={totalCount}
          pageIndex={pageIndex}
          onPageChange={handlePageChange}
          onPageSizeChange={setPageSize}
          onSearchChange={onSearchChange}
          onFiltersChange={() => setPageIndex(0)}
          buttons={
            <>
              <TableMenuButton
                startIcon={<MailOutlineIcon />}
                onClick={onMessageVolunteers}
                disabled={!selectedRowsCount}
                rightMargin
              >
                {labels.message}
              </TableMenuButton>
              <ExportChampionsButton
                data={selectedRows}
                disabled={!selectedRowsCount}
                includeDonations={isSuperAdmin}
                exportLabel={labels.export}
              />
            </>
          }
          popover={
            <>
              <TablePopoverButton
                startIcon={<VerifiedIcon style={{ width: '20px', height: '20px' }} />}
                disabled={!selectedUnverifiedRowsCount || sendNotificationLoading}
                onClick={() => handleResendVerification(selectedUnverifiedRows)}
              >
                {labels.resendVerification}
              </TablePopoverButton>

              {chatsEnabled && (
                <TablePopoverButton startIcon={<MessageOutlinedIcon />} onClick={handleChat}>
                  {labels.addToChat}
                </TablePopoverButton>
              )}
              {bulkAddMembersEnabled && (
                <TablePopoverButton startIcon={<GroupOutlinedIcon />} onClick={onBulkAddMembers}>
                  {labels.addToGroup}
                </TablePopoverButton>
              )}
              {championsTagsEnabled && (
                <TablePopoverButton startIcon={<LabelOutlinedIcon />} onClick={onUpdateTags}>
                  {labels.addRemoveTags}
                </TablePopoverButton>
              )}
              <ExportChampionsMenu
                disabled={!selectedRowsCount}
                exportToLabel={labels.exportTo}
                options={[
                  {
                    label: labels.littleGreenLight,
                    onClick: handlePushChampions,
                    enabled:
                      isLglIntegrationEnabled &&
                      lglChampionsSyncEnabled &&
                      integrationKey.enabled &&
                      !!integrationKey.token,
                  },
                  {
                    label: labels.donorPerfect,
                    onClick: handlePushChampionsToDonorPerfect,
                    enabled:
                      isDonorPerfectIntegrationEnabled &&
                      donorPerfectIntegrationKey.enabled &&
                      !!donorPerfectIntegrationKey.token,
                  },
                  {
                    label: labels.fastn,
                    onClick: handlePushChampionsToFastn,
                    enabled: isFastnIntegrationEnabled && fastnIntegrationKey.enabled && !!fastnIntegrationKey.token,
                  },
                ]}
              />
              <TablePopoverButton
                startIcon={<RemoveCircleOutlineOutlinedIcon />}
                disabled={!selectedRowsCount}
                onClick={onRemoveVolunteer}
              >
                {selectedRowsCount > 1 ? labels.removeChampions : labels.removeChampion}
              </TablePopoverButton>
              {showImportButton && (
                <TablePopoverButton startIcon={<SaveAltOutlinedIcon />} onClick={showImportVolunteerPrompt}>
                  {labels.importVolunteers}
                </TablePopoverButton>
              )}
            </>
          }
          addButton={
            <ContainedButton startIcon={<AddIcon />} onClick={addVolunteer}>
              {labels.addChampion}
            </ContainedButton>
          }
          useDateRange
          skipReset={skipReset}
        />
      </Grid>
    </ChampionsServerSideSelectionProvider>
  )
}
